import * as React from 'react';
import { useRef, useState } from 'react';
import { Popover, PopoverTrigger } from '@/shared/components/ui/popover';
import { useInfiniteSelect } from './useInfiniteSelect';
import { AsyncComboboxTrigger } from './AsyncComboboxTrigger';
import { AsyncComboboxContent } from './AsyncComboboxContent';
import type { AsyncComboboxProps, AsyncComboboxOption } from './AsyncCombobox.types';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_STALE_TIME = 5 * 60 * 1000;

export function AsyncCombobox<T extends AsyncComboboxOption = AsyncComboboxOption>(
  props: AsyncComboboxProps<T>,
) {
  const {
    queryKey,
    fetchFn,
    staticOptions,
    placeholder,
    searchPlaceholder,
    emptyMessage,
    disabled = false,
    className,
    triggerClassName,
    'data-testid': testId,
    pageSize = DEFAULT_PAGE_SIZE,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    staleTime = DEFAULT_STALE_TIME,
    renderOption,
    renderValue,
    selectedOptions: selectedOptionsProp,
    onOpenChange: onOpenChangeProp,
  } = props;

  const isMulti = props.mode === 'multi';
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Static mode client-side filter
  const [staticSearch, setStaticSearch] = useState('');
  const useStatic = !!staticOptions;

  const infiniteSelect = useInfiniteSelect<T>({
    queryKey,
    fetchFn,
    pageSize,
    enabled: isOpen && !useStatic,
    staleTime,
    debounceMs,
  });

  const filteredStaticOptions = (() => {
    if (!useStatic || !staticOptions) return [];
    if (!staticSearch) return staticOptions;
    const lower = staticSearch.toLowerCase();
    return staticOptions.filter(
      (o) => o.label.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower),
    );
  })();

  const options = useStatic ? filteredStaticOptions : infiniteSelect.options;
  const isLoading = useStatic ? false : infiniteSelect.isLoading;
  const isFetchingNextPage = useStatic ? false : infiniteSelect.isFetchingNextPage;
  const hasNextPage = useStatic ? false : infiniteSelect.hasNextPage;
  const fetchNextPage = useStatic ? () => {} : infiniteSelect.fetchNextPage;
  const search = useStatic ? staticSearch : infiniteSelect.search;
  const setSearch = useStatic ? setStaticSearch : infiniteSelect.setSearch;

  // Selected IDs
  const selectedIds: string[] = isMulti
    ? (props.value as string[])
    : props.value
      ? [props.value]
      : [];

  // Resolve option objects — merges loaded pages + pre-loaded selectedOptions prop
  const allKnownOptions = (() => {
    const map = new Map<string, T>();
    (staticOptions ?? []).forEach((o) => map.set(o.id, o));
    options.forEach((o) => map.set(o.id, o));
    (selectedOptionsProp ?? []).forEach((o) => map.set(o.id, o));
    return map;
  })();

  const selectedOptionObjects = selectedIds
    .map((id) => allKnownOptions.get(id))
    .filter((o): o is T => !!o);

  const singleSelectedOption = !isMulti ? selectedOptionObjects[0] : undefined;

  // Selection handlers
  const handleSelect = (option: T) => {
    if (isMulti) {
      const current = props.value as string[];
      const alreadySelected = current.includes(option.id);
      const maxSel = (props as { maxSelections?: number }).maxSelections;
      if (!alreadySelected && maxSel !== undefined && current.length >= maxSel) return;
      props.onChange(
        alreadySelected ? current.filter((id) => id !== option.id) : [...current, option.id],
      );
      // Multi: keep popover open
    } else {
      props.onChange(option.id);
      setIsOpen(false);
      setSearch('');
      setHighlightedIndex(-1);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isMulti) props.onChange(null);
  };

  const handleRemoveBadge = (id: string) => {
    if (isMulti) {
      props.onChange((props.value as string[]).filter((v) => v !== id));
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChangeProp?.(open);
    if (!open) {
      setSearch('');
      setHighlightedIndex(-1);
    }
  };

  // Keyboard navigation on the search input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => {
          const next = Math.min(i + 1, options.length - 1);
          optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => {
          const prev = Math.max(i - 1, 0);
          optionRefs.current[prev]?.scrollIntoView({ block: 'nearest' });
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    // `modal` matches how Radix Select already behaves. Without it the popover
    // layer does not disable outside pointer events, so a click outside is
    // handled by this layer AND by any Dialog underneath — closing both at once
    // and losing whatever the user had typed in the dialog.
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <AsyncComboboxTrigger
          isOpen={isOpen}
          disabled={disabled}
          isMulti={isMulti}
          placeholder={placeholder}
          selectedOption={singleSelectedOption}
          clearable={(props as { clearable?: boolean }).clearable}
          onClear={handleClear}
          selectedOptions={selectedOptionObjects}
          maxDisplayedBadges={(props as { maxDisplayedBadges?: number }).maxDisplayedBadges}
          onRemoveBadge={handleRemoveBadge}
          renderValue={renderValue ? (opt) => renderValue(opt as T) : undefined}
          className={triggerClassName ?? className}
          data-testid={testId}
        />
      </PopoverTrigger>

      <AsyncComboboxContent
        options={options}
        selectedIds={selectedIds}
        highlightedIndex={highlightedIndex}
        isMulti={isMulti}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        search={search}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        onSearch={setSearch}
        onSelect={handleSelect}
        fetchNextPage={fetchNextPage}
        renderOption={renderOption}
        onKeyDown={handleKeyDown}
        optionRefs={optionRefs}
      />
    </Popover>
  );
}
